import React, { useRef, useState } from "react";
import { Spin } from "antd";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { useAudio } from "./hooks/useAudio";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { ILang } from "providers/AudioProvider/types";
import { IVoiceType } from "types";
import { MusicNoteMIcon } from "components/elements/icons/MusicNoteMIcon";
import { useUiLang } from "utils/uiLanguage";
import { useNotificationContext } from "hooks/useNotificationContext";
import { showRevoiceReply } from "providers/AudioProvider/revoiceToast";
import { IconContainerSC } from "./styled";

interface IAudioManageProps {
    lang: ILang;
    subtitleId: string;
    disabled: boolean;
    voice?: IVoiceType;
    currentSpeed?: number;
    setEditing: (id: string) => void;
    /**
     * Почему ♪ сейчас не может озвучить (нет пары перевода, пустой текст, испорченное время) —
     * то, на что сервер заведомо откажет. Значок тогда не зовёт сервер, а подсказка называет
     * причину.
     */
    blockedReason?: string | null;
    /**
     * То же для 🔊 (оригинальный звук): сервер судит его по-своему — текст не нужен, зато конец
     * реплики должен быть позже места. Не передано — как у ♪.
     */
    originalBlockedReason?: string | null;
    /**
     * Сервер прослушал дубль короткой реплики и не услышал её текста даже после всех попыток
     * («озвучка не совпала с текстом: слышно «खारे»»): подсказка ♪ это называет, рядом — ⚠.
     * ♪ при этом работает — переозвучить или исправить текст и есть то, что нужно.
     */
    checkWarning?: string | null;
}

/**
 * ♪ (озвучить перевод) и 🔊 (озвучить оригинальным голосом) в строке перевода.
 *
 * 30.09 переводчик нажал ♪ десять раз: значок выглядел одинаково до, во время и после каждого
 * отказа. Теперь пока идёт озвучка, вместо ♪ крутится знак занятости, второе нажатие ничего не
 * делает, и у обоих значков есть подсказка; отказ сервера провайдер называет в полосе ошибок.
 */
export const AudioManage: React.FC<IAudioManageProps> = ({
    lang,
    subtitleId,
    voice: currentVoice,
    disabled,
    currentSpeed,
    setEditing,
    blockedReason,
    originalBlockedReason,
    checkWarning,
}) => {
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const notify = useNotificationContext();
    const en = useUiLang() === "en";
    const [voice] = useState<IVoiceType>(currentVoice || "male");
    const [speed] = useState<number>(() => currentSpeed || 1);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);

    const { saveAudio } = useAudio({
        handles,
        subtitleId,
        lang,
        folder: state.folder,
        mediaId: state.mediaId,
        useCheckedVoiceoverSynthesis: state.useCheckedVoiceoverSynthesis,
    });

    const originalReason = originalBlockedReason !== undefined ? originalBlockedReason : blockedReason;

    const run = async (synt_audio_type?: "original_ru") => {
        const reason = synt_audio_type === "original_ru" ? originalReason : blockedReason;
        if (disabled || reason || busyRef.current) {
            return;
        }
        busyRef.current = true;
        setBusy(true);
        try {
            const result = await saveAudio(voice, speed, synt_audio_type);
            // Ответ ♪ тостом (проба P50m10d, E6: после ♪ на странице не было ни успеха, ни ошибки): принят, новый
            // дубль и его знаки, голос, ушла ли правка в эфир — или отказ словами сервера.
            if (synt_audio_type !== "original_ru") showRevoiceReply(notify, result, subtitleId, en);
        } catch (e) {
            // Провайдер свои отказы уже назвал; сюда долетает только непредвиденное.
            console.error("[♪] voicing failed:", e);
            handles.showEditorError?.(`Озвучка не создана: ${(e as Error)?.message || e}`);
            showRevoiceReply(notify, { error: (e as Error)?.message || String(e) }, subtitleId, en);
        } finally {
            busyRef.current = false;
            setBusy(false);
            setEditing("");
        }
    };

    const noteInactive = disabled || Boolean(blockedReason) || busy;
    const originalInactive = disabled || Boolean(originalReason) || busy;
    const busyTitle = en ? "Voicing…" : "Озвучивается…";
    const noteBase = blockedReason || (en ? "Voice the translation" : "Озвучить перевод");
    const noteTitle = busy ? busyTitle : checkWarning ? `${noteBase} — ⚠ ${checkWarning}` : noteBase;
    const originalTitle = busy
        ? busyTitle
        : originalReason
          ? `${en ? "Original voice (RU)" : "Оригинальная озвучка (RU)"}: ${originalReason}`
          : en
            ? "Original voice (RU)"
            : "Оригинальная озвучка (RU)";

    return (
        <>
            <IconContainerSC
                title={noteTitle}
                data-voicing="tts"
                data-voicing-busy={busy ? "true" : undefined}
                style={{
                    ...(noteInactive ? { opacity: busy ? 1 : 0.4, cursor: busy ? "wait" : "no-drop" } : {}),
                    ...(checkWarning ? { position: "relative" as const } : {}),
                }}
            >
                {busy ? (
                    <Spin size="small" />
                ) : (
                    <MusicNoteMIcon fontSize={"1.3em"} onClick={() => void run()} />
                )}
                {checkWarning && !busy && (
                    <span
                        data-voicing-check="mismatch"
                        title={checkWarning}
                        style={{
                            position: "absolute",
                            top: -6,
                            right: -8,
                            fontSize: 10,
                            lineHeight: 1,
                            color: "#d4380d",
                            pointerEvents: "none",
                        }}
                    >
                        ⚠
                    </span>
                )}
            </IconContainerSC>
            <IconContainerSC
                title={originalTitle}
                data-voicing="original_ru"
                style={originalInactive ? { opacity: 0.4, cursor: busy ? "wait" : "no-drop" } : undefined}
            >
                <span
                    role="img"
                    aria-label={originalTitle}
                    style={{ cursor: originalInactive ? "inherit" : "pointer", fontSize: "1.1em" }}
                    onClick={() => void run("original_ru")}
                >
                    🔊
                </span>
            </IconContainerSC>
        </>
    );
};
